import type { FragmentType } from 'src/gql';
import { useRouter } from 'next/navigation';
import { TableBodySkeleton } from '@savvy-kit/components/skeleton';
import { TableBody, TableRow } from '@savvy-kit/components/table';
import { isNil } from 'es-toolkit';
import { graphql, unmaskFragment } from 'src/gql';

import type { SelectedClient } from '../tax-utils';
import type { TaxOverviewTableColumn } from './tax-overview-table-columns';
import { TaxOverviewTableBodyRow } from './tax-overview-table-body-row';

export const TaxOverviewTableBodyFragment = graphql(/* GraphQL */ `
  fragment TaxOverviewTableBody on TaxEnrollmentsTableRowConnection {
    nodes {
      ...TaxOverviewTableBodyRow
      rowId
      clientId
      salesforceId
      taxEnrollmentId
    }
  }
`);

type Props = {
  readonly queryFrag: FragmentType<typeof TaxOverviewTableBodyFragment> | undefined;
  readonly loading: boolean;
  readonly columns: readonly TaxOverviewTableColumn[];
  readonly selectedClients: SelectedClient[];
  readonly toggleRow?: (selectedClient: SelectedClient, checked: boolean) => void;
  readonly onEditClick: (salesforceId: string | null) => void;
};

export function TaxOverviewTableBody({ queryFrag, loading, columns, selectedClients, toggleRow, onEditClick }: Props) {
  const router = useRouter();
  const data = unmaskFragment(TaxOverviewTableBodyFragment, queryFrag);

  if (loading) {
    return <TableBodySkeleton rows={8} columns={columns.length} />;
  }

  if (isNil(data) || isNil(data.nodes)) {
    return null;
  }

  const taxData = data.nodes.filter((row) => !isNil(row));

  const handleRowCheckedChange = (checkedClient: SelectedClient, checked: boolean) => {
    toggleRow?.(checkedClient, checked);
  };

  return (
    <TableBody className="w-full">
      {taxData.map((row) => {
        const rowId = row.rowId;
        const salesforceId = row.salesforceId;
        const checkedClient = { id: rowId, clientId: row.clientId, taxEnrollmentId: row.taxEnrollmentId, salesforceId };
        return (
          <TableRow
            key={rowId}
            className="flex items-center cursor-pointer px-4 min-h-12 border-slate-200"
            onClick={() => {
              if (!isNil(salesforceId)) {
                router.push(`/advisor/dashboard/client/${salesforceId}/tax`);
              }
            }}
          >
            <TaxOverviewTableBodyRow
              rowFrag={row}
              columns={columns}
              checked={selectedClients.some((client) => client.id === rowId)}
              onCheckedChange={(checked) => {
                handleRowCheckedChange(checkedClient, checked);
              }}
              onEditClick={() => {
                onEditClick(salesforceId);
              }}
            />
          </TableRow>
        );
      })}
    </TableBody>
  );
}
